import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * The one sheet style (Brian, Oct 22: "one sheet style"): frosted glass from the
 * bottom on the phone, a centred glass panel on the desktop. Renders in place
 * with position: fixed so it keeps the .gg tokens.
 */
export function Sheet({ open, onClose, label, children, className }: {
  open: boolean
  onClose: () => void
  label: string
  children: ReactNode
  className?: string
}) {
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', k)
    return () => document.removeEventListener('keydown', k)
  }, [open, onClose])
  return (
    <>
      <div className={`fx-scrim${open ? ' on' : ''}`} onClick={onClose} aria-hidden="true" />
      <div className={`fx-sheet${open ? ' on' : ''}${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={label} aria-hidden={!open}>
        <div className="fx-grab" />
        {children}
      </div>
    </>
  )
}

/** A short note at the foot of the screen (Brian's .toast). */
export function useToast(): [ReactNode, (body: ReactNode, ms?: number) => void] {
  const [body, setBody] = useState<ReactNode>(null)
  const [on, setOn] = useState(false)
  const timer = useRef<number | undefined>(undefined)
  const show = useCallback((b: ReactNode, ms = 2600) => {
    setBody(b); setOn(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setOn(false), ms)
  }, [])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  return [<div key="toast" className={`fx-toast${on ? ' on' : ''}`} role="status" aria-live="polite">{body}</div>, show]
}

/** Remembered on this device only (folds, the hidden key). */
export function useLocalFlag<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try { const raw = localStorage.getItem(key); return raw == null ? initial : JSON.parse(raw) as T } catch { return initial }
  })
  useEffect(() => {
    try { const raw = localStorage.getItem(key); setV(raw == null ? initial : JSON.parse(raw) as T) } catch { /* keep */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const set = useCallback((next: T) => {
    setV(next)
    try { localStorage.setItem(key, JSON.stringify(next)) } catch { /* private mode */ }
  }, [key])
  return [v, set]
}

export const Icon = {
  lock: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>,
  unlock: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 7.5-2" /></svg>,
  x: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>,
  clock: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>,
  fold: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>,
  pin: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6-5.2-6-10a6 6 0 0 1 12 0c0 4.8-6 10-6 10z" /><circle cx="12" cy="11" r="2.2" /></svg>,
  chev: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>,
}
